import React from "react";
import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import Logo from "../../Images/logo.png";
import { APP_VERSION } from "../../config";
import { motion, radius, transition } from "../../theme/tokens";
import { authGlass, authTokens as t } from "../../theme/authTokens";

/**
 * The authentication shell.
 *
 * One layout, two screens: sign-in and account recovery both render inside it,
 * so the branding panel, background, card geometry, type scale and footer are
 * defined exactly once. Only `children` — the card body — differs between them.
 *
 * Nothing here is interactive and nothing here touches authentication state.
 */

const FEATURES = [
  "Client Management",
  "Policy Management",
  "Task & Follow-up Tracking",
  "Reports & Business Operations",
] as const;

/** A single glass feature row on the branding panel. */
const FeatureRow = ({ label }: { label: string }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      px: 1.75,
      py: 1.15,
      borderRadius: `${radius.md}px`,
      background: authGlass.background,
      border: authGlass.border,
    }}
  >
    <Box
      aria-hidden
      sx={{
        width: 22,
        height: 22,
        flexShrink: 0,
        borderRadius: "50%",
        display: "grid",
        placeItems: "center",
        border: `1px solid ${alpha(t.cyanLight, 0.55)}`,
        backgroundColor: alpha(t.cyan, 0.18),
      }}
    >
      <CheckRoundedIcon sx={{ fontSize: 13, color: t.cyanLight }} />
    </Box>
    <Typography sx={{ fontSize: 14, lineHeight: 1.4, color: alpha("#ffffff", 0.9) }}>
      {label}
    </Typography>
  </Box>
);

/** The small brand pill at the top of each card. */
export const AuthBadge = () => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.85,
      px: 1.35,
      py: 0.5,
      mb: 2.25,
      borderRadius: `${radius.pill}px`,
      border: `1px solid ${alpha(t.blue, 0.18)}`,
      backgroundColor: alpha(t.blue, 0.06),
    }}
  >
    <Box
      aria-hidden
      sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: t.cyan }}
    />
    <Typography
      component="span"
      sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.13em", color: t.blue }}
    >
      GT BROKERS PORTAL
    </Typography>
  </Box>
);

/**
 * The card the two screens share: width, radius, border, shadow and padding.
 * Screen-specific content is passed as children.
 */
export const AuthCard: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      width: "100%",
      maxWidth: t.cardWidth,
      backgroundColor: t.surface,
      border: `1px solid ${alpha("#1E3A5F", 0.07)}`,
      borderRadius: "24px",
      boxShadow: t.cardShadow,
      px: { xs: 2.75, sm: 4.5 },
      py: { xs: 3.25, sm: 4.25 },
      animation: `gtAuthRise ${motion.slow + 60}ms ${motion.easing} both`,
      "@keyframes gtAuthRise": {
        from: { opacity: 0, transform: "translateY(8px)" },
        to: { opacity: 1, transform: "none" },
      },
      "@media (prefers-reduced-motion: reduce)": { animation: "none" },
    }}
  >
    {children}
  </Box>
);

const AuthLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component="main"
    sx={{
      display: "flex",
      width: "100%",
      height: "100vh",
      /* 100dvh where supported: mobile browser chrome no longer crops the card,
         and an open keyboard does not fight a fixed 100vh. */
      "@supports (height: 100dvh)": { height: "100dvh" },
      overflowX: "hidden",
      backgroundColor: t.pageBg,
    }}
  >
    {/* ============================================ left — branding panel */}
    <Box
      sx={{
        /* Hidden below md; a compact navy header replaces it there. */
        display: { xs: "none", md: "flex" },
        flexDirection: "column",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        flex: { md: "0 0 42%", lg: "0 0 50%" },
        px: { md: 5, lg: 8, xl: 10 },
        py: 6,
        color: t.onDark,
        backgroundImage: `linear-gradient(152deg, ${t.navyRaised} 0%, ${t.navy} 46%, ${t.navyDeep} 100%)`,
      }}
    >
      {/* Grid ruling, faded out toward the edges so it reads as texture. */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage: [
            "linear-gradient(rgba(255, 255, 255, 0.035) 1px, transparent 1px)",
            "linear-gradient(90deg, rgba(255, 255, 255, 0.035) 1px, transparent 1px)",
          ].join(", "),
          backgroundSize: "48px 48px",
          maskImage: "radial-gradient(80% 75% at 45% 45%, #000 0%, transparent 100%)",
          WebkitMaskImage: "radial-gradient(80% 75% at 45% 45%, #000 0%, transparent 100%)",
        }}
      />

      {/* Two cool glows — atmosphere, not a filter. */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage: [
            `radial-gradient(46% 44% at 30% 24%, ${alpha(t.cyan, 0.2)} 0%, ${alpha(t.cyan, 0)} 70%)`,
            `radial-gradient(52% 46% at 78% 88%, ${alpha(t.blue, 0.26)} 0%, ${alpha(t.blue, 0)} 72%)`,
          ].join(", "),
        }}
      />

      {/* Faint decorative glass rectangles, mirroring the reference. */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          top: 72,
          left: 44,
          width: 152,
          height: 268,
          borderRadius: `${radius.sm}px`,
          border: `1px solid ${alpha("#ffffff", 0.05)}`,
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          right: -60,
          bottom: -40,
          width: 320,
          height: 320,
          borderRadius: `${radius.lg}px`,
          background: alpha("#ffffff", 0.022),
          border: `1px solid ${alpha("#ffffff", 0.04)}`,
          transform: "rotate(-8deg)",
        }}
      />

      {/* ------------------------------------------------------- content */}
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          maxWidth: 460,
          mx: "auto",
          width: "100%",
          animation: `gtAuthBrand ${motion.slow + 200}ms ${motion.easing} both`,
          "@keyframes gtAuthBrand": {
            from: { opacity: 0, transform: "translateY(10px)" },
            to: { opacity: 1, transform: "none" },
          },
          "@media (prefers-reduced-motion: reduce)": { animation: "none" },
        }}
      >
        {/* The wordmark is dark-on-transparent, so it is rendered as a white
            monochrome mark on the navy panel — the brand blue returns
            immediately below, on the rule and the tagline. */}
        <Box
          component="img"
          src={Logo}
          alt="GT Brokers"
          sx={{
            width: "100%",
            maxWidth: { md: 250, lg: 296 },
            height: "auto",
            display: "block",
            filter: "brightness(0) invert(1)",
          }}
        />

        <Box
          sx={{
            width: 56,
            height: 2,
            mt: 3,
            mb: 3.5,
            borderRadius: 2,
            backgroundImage: `linear-gradient(90deg, ${t.cyan}, ${alpha(t.cyan, 0)})`,
          }}
        />

        <Typography
          component="p"
          sx={{
            fontSize: { md: 38, lg: 44 },
            fontWeight: 700,
            lineHeight: 1.1,
            letterSpacing: "-0.028em",
          }}
        >
          Welcome Back
        </Typography>

        <Typography
          sx={{
            mt: 1.75,
            fontSize: { md: 11.5, lg: 12.5 },
            fontWeight: 700,
            letterSpacing: "0.13em",
            color: t.cyanLight,
          }}
        >
          INSURANCE • CLIENTS • POLICIES • SERVICE
        </Typography>

        <Typography
          sx={{
            mt: 2.25,
            maxWidth: 400,
            fontSize: 14.5,
            lineHeight: 1.7,
            color: t.onDarkMuted,
          }}
        >
          Manage your clients, policies, tasks and daily operations from one
          secure workspace.
        </Typography>

        <Box sx={{ mt: 4, display: "flex", flexDirection: "column", gap: 1.25, maxWidth: 340 }}>
          {FEATURES.map((feature) => (
            <FeatureRow key={feature} label={feature} />
          ))}
        </Box>
      </Box>

      {/* Product label. The version is the build's own — see config/index.ts. */}
      <Typography
        sx={{
          position: "absolute",
          bottom: 28,
          left: 0,
          right: 0,
          zIndex: 1,
          textAlign: "center",
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: "0.16em",
          color: t.onDarkFaint,
        }}
      >
        {`GT BROKERS PORTAL · V${APP_VERSION}`}
      </Typography>
    </Box>

    {/* =========================================== right — authentication */}
    <Box
      sx={{
        position: "relative",
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        /* The panel — not the page — scrolls if a short viewport or an open
           keyboard leaves the card taller than the screen. */
        overflowY: "auto",
        backgroundColor: t.pageBg,
      }}
    >
      {/* Extremely subtle blurred gradient shapes. */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          backgroundImage: [
            `radial-gradient(38% 34% at 88% 8%, ${alpha(t.cyan, 0.07)} 0%, ${alpha(t.cyan, 0)} 72%)`,
            `radial-gradient(42% 36% at 6% 96%, ${alpha(t.blue, 0.06)} 0%, ${alpha(t.blue, 0)} 74%)`,
          ].join(", "),
        }}
      />

      {/* Compact branding header — small screens only, standing in for the
          panel above rather than squeezing it alongside the card. */}
      <Box
        sx={{
          display: { xs: "flex", md: "none" },
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          px: 2.5,
          py: 2.25,
          backgroundImage: `linear-gradient(120deg, ${t.navyRaised} 0%, ${t.navy} 100%)`,
        }}
      >
        {/* Empty alt: the branding panel above is display:none here but still in
            the DOM, so its wordmark already carries the accessible name. */}
        <Box
          component="img"
          src={Logo}
          alt=""
          aria-hidden
          sx={{ width: "100%", maxWidth: 178, height: "auto", filter: "brightness(0) invert(1)" }}
        />
      </Box>

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: { xs: 2.25, sm: 3 },
          py: { xs: 3, sm: 4 },
          minWidth: 0,
        }}
      >
        {children}
      </Box>

      <Typography
        sx={{
          position: "relative",
          zIndex: 1,
          flexShrink: 0,
          textAlign: "center",
          pb: { xs: 2.5, sm: 3 },
          px: 2,
          fontSize: 10.5,
          fontWeight: 600,
          letterSpacing: "0.16em",
          color: alpha(t.muted, 0.75),
          /* The link inherits the footer's tone so it reads as one quiet line
             rather than a stray blue anchor under the card. */
          "& a": {
            color: "inherit",
            textDecoration: "none",
            transition: transition(["color"], motion.fast),
            "&:hover": { color: t.blue, textDecoration: "underline" },
            "&:focus-visible": {
              outline: `2px solid ${alpha(t.blue, 0.5)}`,
              outlineOffset: 2,
            },
          },
        }}
      >
        POWERED BY{" "}
        <a href="https://vmwebs.com/" target="_blank" rel="noreferrer">
          vmwebs
        </a>
      </Typography>
    </Box>
  </Box>
);

export default AuthLayout;
